import {
  createContext,
  useState,
  ReactNode,
  useEffect,
  Dispatch,
  SetStateAction,
  useCallback,
  useRef,
} from 'react'

import { UserProfileRole } from '../modules/users/types/UserProfile'
import { growthbook } from '../plugins/growthbook'
import { SESSION_EXPIRED_EVENT, resetSessionExpiredNotice } from '../utils/sessionExpiry'
import { TOKEN_REFRESHED_EVENT } from '../utils/tokenRefresh'

import UserData from '@/interfaces/UserData'
import SelectedPair from '@/interfaces/SelectedPair'
import useBranchesFetcher from '@/hooks/useBranchesFetcher'
import useBodiesFetcher from '@/hooks/useBodiesFetcher'
import { BranchState } from '@/enums/DocStates'
import { useTokenExpiration } from '@/hooks/useTokenExpiration'
import useRenewToken from '@/hooks/useRenewToken'
import { UserProfile } from '@/modules/users/types/UserProfile'
import { User } from '@/modules/users/types/User'
import { useUnreadMessageCount } from '@/hooks/useUnreadMessageCount'
import { useToast } from '@/components/ToastProvider'


// Define a type for the context value
export interface AuthContextType {
  isAuthenticated: boolean
  setIsAuthenticated: (data: boolean) => void
  signIn: (user: User, profile: UserProfile) => void
  signOut: () => void
  userData: UserData | null // Can be null if user is not authenticated
  setUserData: Dispatch<SetStateAction<UserData | null>>
  selectedPair: SelectedPair | null
  setSelectedPair: (data: SelectedPair) => void
  // Indica que estamos buscando un vehiculo cuando creamos algun documento
  vehicleForDoc: number // Indica que estamos creando un vehiculo desde
  setVehicleForDoc: (data: number) => void
  authToken: string
  isDirty: boolean
  setIsDirty: (dirty: boolean) => void
  showWarningModal: boolean
  renewManually: () => void
  hasAllRoles: (...roles: UserProfileRole[]) => boolean
  hasAnyRole: (...roles: UserProfileRole[]) => boolean
  hasRole: (role: UserProfileRole) => boolean
  // Unread messages: consultas alone (the counter dealers already know) and
  // consultas + otros (the whole inbox). The menu badge shows both.
  unreadConsultasCount: number
  unreadMessagesCount: number
  unreadDealsCount: number
  isUnreadCountConnected: boolean
}

// Create the context with an initial value of undefined
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Define the provider component that will wrap parts of your app
interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { fetchBranches } = useBranchesFetcher()
  const { fetchBodiesData } = useBodiesFetcher()

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    // Get the initial authentication state from localStorage
    const storedAuth = localStorage.getItem('isAuthenticated')

    return storedAuth === 'true' // Convert the string back to boolean
  })

  const [userData, setUserData] = useState<UserData | null>(() => {
    // Get the initial userData from localStorage
    const storedUserData = localStorage.getItem('userData')

    return storedUserData ? JSON.parse(storedUserData) : null
  })

  const [selectedPair, setSelectedPair] = useState<SelectedPair | null>(() => {
    // Get the initial userData from localStorage
    const storedSelected = localStorage.getItem('selectedPair')

    return storedSelected ? JSON.parse(storedSelected) : null
  })
  const [vehicleForDoc, setVehicleForDoc] = useState<number>(() => {
    const aux = localStorage.getItem('vehicleForDoc')

    return Number(aux ?? 0)
  })
  const [authToken, setAuthToken] = useState<string>(() => {
    const aux = localStorage.getItem('authToken')

    return aux ?? ''
  })

  const [showWarningModal, setShowWarningModal] = useState(false)
  const { renewToken } = useRenewToken()

  const [isDirty, setIsDirty] = useState(false) // Add dirty state

  // Toast functionality for new messages
  const { showToast } = useToast()
  
  // Callback for when message count increases
  const handleMessageCountIncrease = useCallback((newCount: number, previousCount: number) => {
    const increment = newCount - previousCount
    if (increment === 1) {
      showToast('Tienes un nuevo mensaje', 'info')
    } else {
      showToast(`Tienes ${increment} nuevos mensajes`, 'info')
    }
  }, [showToast])

  // Callback for when deal count increases
  const handleDealCountIncrease = useCallback((newCount: number, previousCount: number) => {
    const increment = newCount - previousCount
    if (increment === 1) {
      showToast('Tienes un nuevo negocio', 'success')
    } else {
      showToast(`Tienes ${increment} nuevos negocios`, 'success')
    }
  }, [showToast])





  // Unread message count hook with toast callbacks
  const { 
    consultasCount: unreadConsultasCount,
    messagesCount: unreadMessagesCount,
    dealsCount: unreadDealsCount,
    isConnected: isUnreadCountConnected 
  } = useUnreadMessageCount(
    userData?.organization?._id || null,
    isAuthenticated,
    handleMessageCountIncrease,
    handleDealCountIncrease
  )

  useEffect(() => {
    localStorage.setItem('userData', JSON.stringify(userData))

    if (userData?.user?.email) {
      void growthbook.setAttributes({ id: userData.user.email })
    }
  }, [userData])

  useEffect(() => {
    // Store the current authentication state and user data in localStorage
    localStorage.setItem('isAuthenticated', String(isAuthenticated))
  }, [isAuthenticated])

  useEffect(() => {
    localStorage.setItem('selectedPair', JSON.stringify(selectedPair))
  }, [selectedPair])

  useEffect(() => {
    localStorage.setItem('vehicleForDoc', String(vehicleForDoc))
  }, [vehicleForDoc])

  useEffect(() => {
    localStorage.setItem('authToken', String(authToken))
  }, [authToken])

  /*
  Carga de datos de referencia (sucursales y carrocerías) al iniciar sesión.

  Esto era un ciclo infinito. El efecto depende de `userData` y se protegía con
  `branches.length === 0`, pero ante un fallo los hooks devuelven null, así que
  `branches ?? []` seguía vacío y `setUserData({ ...userData, ...data })` creaba un
  objeto nuevo que volvía a cumplir la condición. Resultado: con el token vencido la
  pestaña pedía `/api/bodies` y `/api/branches/act/:org` unas 10 veces por segundo,
  sin pausa, hasta que alguien la cerrara. Un solo navegador llegó a 121.376 peticiones
  en cuatro horas —el 87% de todo el tráfico de la API— y tumbó los logins.

  Dos cambios: la carga se intenta una vez por par usuario+organización (el ref, que no
  depende de la identidad del objeto), y sólo se escribe el estado si la carga
  realmente trajo datos. Un fallo ya no puede realimentar el efecto.
  */
  const referenceDataKey = useRef<string | null>(null)

  useEffect(() => {
    const userId = userData?.user?._id
    const organizationId = userData?.organization?._id

    if (!userId || !organizationId) return

    if (userData?.branches?.length) return

    const key = `${userId}:${organizationId}`

    if (referenceDataKey.current === key) return

    referenceDataKey.current = key

    const fetchDataAsync = async () => {
      try {
        // Make both API calls in parallel
        const [branches, bodies] = await Promise.all([
          fetchBranches(organizationId, BranchState.Activo),
          fetchBodiesData(),
        ])

        // Los hooks devuelven null cuando la petición falla. En ese caso no guardamos
        // nada: escribir el resultado vacío es justamente lo que realimentaba el ciclo.
        // Liberamos la llave para poder reintentar si algo más cambia la sesión (por
        // ejemplo una renovación de token), pero eso ya no se dispara solo.
        if (!branches || !bodies) {
          referenceDataKey.current = null

          return
        }

        const sortedBodies = [...bodies].sort((a, b) =>
          a.description.localeCompare(b.description, undefined, { sensitivity: 'base' })
        )

        setUserData((previous) => (previous ? { ...previous, branches, bodies: sortedBodies } : previous))
      } catch (error) {
        referenceDataKey.current = null
        console.error('Error fetching data:', error)
      }
    }

    void fetchDataAsync()
  }, [userData])

  const signIn = useCallback((user: User, profile: UserProfile) => {
    setSelectedPair({ vehicleId: '', clientId: '' })
    setIsAuthenticated(true)
    setAuthToken(user.token)
    setVehicleForDoc(0)
    referenceDataKey.current = null
    // Sesión nueva: si vence más adelante, el aviso tiene que volver a emitirse.
    resetSessionExpiredNotice()

    // Set login timestamp for unified notifications
    const loginTimestamp = Date.now().toString()
    localStorage.setItem('loginTimestamp', loginTimestamp)

    setUserData({
      organization: profile.body.organization,
      branches: [],
      bodies: [],
      profile,
      user: {
        ...user,
        fullname: `${user.firstname} ${user.lastname}`,
      },
    })
  }, [])

  const signOut = useCallback(() => {
    setShowWarningModal(false)
    setIsAuthenticated(false)
    setSelectedPair(null)
    setVehicleForDoc(0)
    setUserData(null)
    setAuthToken('')

    localStorage.removeItem('isAuthenticated')
    localStorage.removeItem('vehicleForDoc')
    localStorage.removeItem('selectedPair')
    localStorage.removeItem('authToken')
    localStorage.removeItem('userData')
    localStorage.removeItem('loginTimestamp') // Clear login timestamp for unified notifications
    referenceDataKey.current = null
  }, [])

  /*
  Un 401 en cualquier petición termina la sesión. Antes no la terminaba en ningún lado:
  `utils/api.ts` sólo tenía interceptor de request y `customFetch` trataba el 401 como
  un error más, así que la pestaña seguía indefinidamente con un token vencido. El
  pestillo de sessionExpiry.ts garantiza que un aluvión de 401 en paralelo produzca un
  único cierre de sesión.
  */
  useEffect(() => {
    const handleSessionExpired = () => {
      signOut()
      location.pathname = '/users/sign-out'
    }

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)

    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
  }, [signOut])

  /*
  Adopcion del token renovado que devuelve la API (ver utils/tokenRefresh.ts).

  Hay que subirlo al estado de React, no alcanza con localStorage: las peticiones leen
  de ahi y andarian bien, pero `useTokenExpiration` arma el temporizador de cierre a
  partir de `authToken`. Con el token viejo en el estado, ese temporizador dispara sobre
  el `exp` anterior y cierra la sesion de alguien que tiene un token valido.

  El listener de `storage` cubre el caso de varias pestañas: el evento lo emite el
  navegador en las OTRAS pestañas cuando una escribe localStorage, asi que la que renovo
  arrastra a las demas y no se pisan renovando cada una por su lado. A proposito solo se
  adoptan valores no vacios: un cierre de sesion en otra pestaña no debe arrastrar a
  esta por este camino.
  */
  useEffect(() => {
    const adopt = (renewed: string) => {
      if (!renewed) return

      setAuthToken((previous) => (previous === renewed ? previous : renewed))
    }

    const handleRefreshed = (event: Event) => adopt((event as CustomEvent<string>).detail)

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== 'authToken') return

      adopt(event.newValue ?? '')
    }

    window.addEventListener(TOKEN_REFRESHED_EVENT, handleRefreshed)
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener(TOKEN_REFRESHED_EVENT, handleRefreshed)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  // Cuando el usuario acepta renovar el token
  const renewManually = async () => {
    if (!userData?.profile?.email) {
      throw new Error('Invalid session state')
    }

    const token = await renewToken(userData.profile.email)

    if (token) {
      setAuthToken(token)
      setShowWarningModal(false)
    }
  }

  const showModal = () => {
    setShowWarningModal(true)
  }

  const onLogout = () => (location.pathname = '/users/sign-out')

  useTokenExpiration(authToken, onLogout, showModal)

  const hasAllRoles = (...roles: UserProfileRole[]): boolean =>
    roles.every((role) => (userData?.profile?.roles ?? []).includes(role))

  const hasAnyRole = (...roles: UserProfileRole[]): boolean =>
    roles.some((role) => (userData?.profile?.roles ?? []).includes(role))

  const hasRole = (role: UserProfileRole): boolean => (userData?.profile?.roles ?? []).includes(role)

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        signIn,
        signOut,
        userData,
        setUserData,
        selectedPair,
        setSelectedPair,
        vehicleForDoc,
        setVehicleForDoc,
        authToken,
        isDirty,
        setIsDirty,
        showWarningModal,
        renewManually,
        hasAllRoles,
        hasAnyRole,
        hasRole,
        unreadConsultasCount,
        unreadMessagesCount,
        unreadDealsCount,
        isUnreadCountConnected,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext }
